import { useState } from 'react';
import { Button, Checkbox, DatePicker, Input, InputNumber, Modal, Select, Switch, Table, TimePicker } from 'antd';
import dayjs from 'dayjs';
import { Plus, Trash2 } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import { tpApi, useTp } from './tpStore';
import { fmtD } from './hrLogic';
import { SHIFT_COLORS, WD, isOvernight, shiftHours, shiftLabel, shiftNightHours, validateShift, type Holiday, type ShiftDef } from './tpLogic';
import { Field, Notes, PageHeader, Panel } from './hrUi';
import { ShiftChip, done, useTpPerms } from './tpUi';

type Draft = Omit<ShiftDef, 'id'> & { id?: string };
const EMPTY: Draft = { code: '', name: '', start: '08:00', end: '17:00', breakMin: 60, work: 1, color: SHIFT_COLORS[0], branchIds: [], active: true };

/** Danh mục ca làm việc + quy định chấm công (đi muộn, ngày nghỉ tuần, trần làm thêm, ngày lễ) */
export default function ShiftSetup() {
  const s = useHr();
  const t = useTp();
  const { canAssign, canTimeEdit, me } = useTpPerms();
  const [edit, setEdit] = useState<Draft | null>(null);
  const [time, setTime] = useState(t.time);
  const [hol, setHol] = useState<Holiday>({ date: '', name: '' });
  const dirty = JSON.stringify(time) !== JSON.stringify(t.time);
  const used = (id: string) => t.assigns.filter(a => a.shiftId === id).length;

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Ca làm việc" desc="Ca dùng để xếp lịch và tính công; ca có khám / thủ thuật bật “cần bác sĩ” để cảnh báo khi xếp thiếu"
          extra={canAssign && <Button type="primary" icon={<Plus size={14} />} onClick={() => setEdit({ ...EMPTY })}>Thêm ca</Button>} />

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
          <Table size="middle" rowKey="id" dataSource={t.shifts} pagination={false} scroll={{ x: 900 }}
            onRow={r => ({ onClick: () => canAssign && setEdit({ ...r }), className: canAssign ? 'cursor-pointer' : '' })}
            columns={[
              { title: 'Ca', key: 'c', width: 220, render: (_, r) => <div className="flex items-center gap-2"><ShiftChip sh={r} /><div><div className="font-medium">{r.name}</div>{!r.active && <div className="text-[11px] text-gray-400">Ngưng dùng</div>}</div></div> },
              { title: 'Giờ', key: 'h', width: 170, render: (_, r) => <div>{shiftLabel(r)}{isOvernight(r) && <span className="text-[11px] text-violet-600"> · qua đêm</span>}<div className="text-[11px] text-gray-400">nghỉ {r.breakMin} phút · {shiftHours(r)} giờ{shiftNightHours(r) ? ` · ${shiftNightHours(r)} giờ đêm` : ''}</div></div> },
              { title: 'Công', key: 'w', width: 70, align: 'right', render: (_, r) => r.work },
              { title: 'Chi nhánh', key: 'b', width: 220, render: (_, r) => <span className="text-xs">{r.branchIds.length ? r.branchIds.map(id => s.branches.find(b => b.id === id)?.short).join(', ') : 'Tất cả'}</span> },
              { title: 'Định mức', key: 'm', width: 170, render: (_, r) => <span className="text-xs">{r.minStaff ? `≥ ${r.minStaff} người` : '—'}{r.needDoctor ? ' · cần bác sĩ' : ''}</span> },
              { title: 'Đã xếp', key: 'u', width: 90, align: 'right', render: (_, r) => <span className="text-xs text-gray-500">{used(r.id)} ca</span> },
            ]} />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Panel title="Quy định chấm công" extra={canTimeEdit && <Button size="small" type="primary" disabled={!dirty} onClick={() => done(tpApi.saveTimeSettings(time, me), 'Đã lưu')}>Lưu</Button>}>
            <div className="p-4 space-y-4 text-sm">
              <Field label="Cho phép đi muộn (phút)" hint="Muộn trong số phút này không tính muộn">
                <InputNumber min={0} max={30} value={time.graceMin} onChange={v => setTime({ ...time, graceMin: v ?? 0 })} disabled={!canTimeEdit} />
              </Field>
              <Field label="Ngày nghỉ hằng tuần (tính công chuẩn)" hint="Công chuẩn = số ngày trong tháng trừ ngày nghỉ tuần; ngày lễ vẫn hưởng lương">
                <Checkbox.Group value={time.weeklyOff} disabled={!canTimeEdit} onChange={v => setTime({ ...time, weeklyOff: v as number[] })}
                  options={[1, 2, 3, 4, 5, 6, 0].map(d => ({ value: d, label: WD[d] }))} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Trần làm thêm / tháng (giờ)" hint="BLLĐ 2019: 40 giờ"><InputNumber min={0} value={time.otMonthCap} disabled={!canTimeEdit} onChange={v => setTime({ ...time, otMonthCap: v ?? 40 })} /></Field>
                <Field label="Trần làm thêm / năm (giờ)" hint="200 giờ; một số ngành 300 giờ"><InputNumber min={0} value={time.otYearCap} disabled={!canTimeEdit} onChange={v => setTime({ ...time, otYearCap: v ?? 200 })} /></Field>
              </div>
            </div>
          </Panel>
          <Panel title={`Ngày lễ, Tết (${time.holidays.length})`}>
            <div className="p-4 space-y-3 text-sm">
              {canTimeEdit && (
                <div className="flex gap-2">
                  <DatePicker format="DD/MM/YYYY" value={hol.date ? dayjs(hol.date) : null} onChange={v => setHol({ ...hol, date: v?.format('YYYY-MM-DD') || '' })} />
                  <Input placeholder="Tên ngày lễ" value={hol.name} onChange={e => setHol({ ...hol, name: e.target.value })} />
                  <Button disabled={!hol.date || !hol.name.trim() || time.holidays.some(h => h.date === hol.date)}
                    onClick={() => { setTime({ ...time, holidays: [...time.holidays, { date: hol.date, name: hol.name.trim() }].sort((a, b) => a.date.localeCompare(b.date)) }); setHol({ date: '', name: '' }); }}>Thêm</Button>
                </div>
              )}
              <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-[260px] overflow-auto">
                {time.holidays.map(h => (
                  <div key={h.date} className="flex items-center gap-2 py-1.5">
                    <span className="w-[110px]">{WD[dayjs(h.date).day()]} {fmtD(h.date)}</span><span className="flex-1">{h.name}</span>
                    {canTimeEdit && <Button size="small" type="text" icon={<Trash2 size={13} />} aria-label="Xóa" onClick={() => setTime({ ...time, holidays: time.holidays.filter(x => x.date !== h.date) })} />}
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-gray-400">Lịch nghỉ Tết / nghỉ bù cập nhật theo thông báo hằng năm. Bấm “Lưu” ở khung bên cạnh để áp dụng.</div>
            </div>
          </Panel>
        </div>
      </div>
      {edit && <ShiftModal d={edit} onClose={() => setEdit(null)} />}
    </Layout>
  );
}

function ShiftModal({ d: init, onClose }: { d: Draft; onClose: () => void }) {
  const s = useHr();
  const t = useTp();
  const { me } = useTpPerms();
  const [d, setD] = useState(init);
  const patch = (p: Partial<Draft>) => setD(x => ({ ...x, ...p }));
  const errors = validateShift(t.shifts, d);
  const tm = (v: string) => dayjs(`2000-01-01T${v}`);
  return (
    <Modal open title={d.id ? `Sửa ca ${init.code}` : 'Thêm ca'} onCancel={onClose} okText="Lưu" okButtonProps={{ disabled: errors.length > 0 }}
      onOk={() => { if (done(tpApi.saveShift(d, me), 'Đã lưu ca')) onClose(); }}>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Mã ca"><Input value={d.code} maxLength={4} onChange={e => patch({ code: e.target.value })} /></Field>
        <Field label="Tên ca"><Input value={d.name} onChange={e => patch({ name: e.target.value })} /></Field>
        <Field label="Bắt đầu"><TimePicker className="w-full" format="HH:mm" minuteStep={15} allowClear={false} value={tm(d.start)} onChange={v => v && patch({ start: v.format('HH:mm') })} /></Field>
        <Field label="Kết thúc" hint={isOvernight(d) ? 'Ca qua đêm — kết thúc ngày hôm sau' : undefined}><TimePicker className="w-full" format="HH:mm" minuteStep={15} allowClear={false} value={tm(d.end)} onChange={v => v && patch({ end: v.format('HH:mm') })} /></Field>
        <Field label="Nghỉ giữa ca (phút)"><InputNumber className="!w-full" min={0} step={15} value={d.breakMin} onChange={v => patch({ breakMin: v ?? 0 })} /></Field>
        <Field label="Công quy đổi" hint={`${shiftHours(d)} giờ làm`}><InputNumber className="!w-full" min={0.1} max={1.5} step={0.5} value={d.work} onChange={v => patch({ work: v ?? 1 })} /></Field>
        <Field label="Áp dụng tại" className="col-span-2">
          <Select mode="multiple" className="w-full" placeholder="Tất cả chi nhánh" value={d.branchIds} onChange={branchIds => patch({ branchIds })} options={s.branches.map(b => ({ value: b.id, label: b.name }))} />
        </Field>
        <Field label="Số người tối thiểu / chi nhánh"><InputNumber className="!w-full" min={0} value={d.minStaff} onChange={v => patch({ minStaff: v || undefined })} /></Field>
        <Field label="Màu"><div className="flex flex-wrap gap-1">{SHIFT_COLORS.map(c => <button key={c} type="button" aria-label="Chọn màu" onClick={() => patch({ color: c })} className={`w-6 h-6 rounded border ${c} ${d.color === c ? 'ring-2 ring-[#3e91ab]' : ''}`} />)}</div></Field>
        <div className="col-span-2 flex flex-wrap gap-4">
          <Checkbox checked={!!d.needDoctor} onChange={e => patch({ needDoctor: e.target.checked || undefined })}>Ca phải có bác sĩ có GPHN</Checkbox>
          <span className="flex items-center gap-2"><Switch size="small" checked={d.active} onChange={active => patch({ active })} />Đang dùng</span>
        </div>
        <div className="col-span-2"><Notes errors={errors} /></div>
      </div>
    </Modal>
  );
}
